import type {
  Tree,
  TreeItemProps,
  WindowedTreeViewProps,
} from '@frameio/vapor';

const TREE_ITEM_TYPES = [
  'all-collections',
  'c2c-connections',
  'collection',
  'folder',
  'hidden-root',
  'root-folder',
  'trash',
  'upload-links',
] as const;

export type ProjectTreeItemType = (typeof TREE_ITEM_TYPES)[number];

export const isValidTreeItemType = (
  activeId: string
): activeId is ProjectTreeItemType =>
  TREE_ITEM_TYPES.some((t) => activeId === t);

export type ProjectTreeItem = {
  id: string;
  type: ProjectTreeItemType;
};

type HiddenRootProps = {
  id: string;
  type: 'hidden-root';
};

export type AllCollectionsProps = {
  id: string;
  type: 'all-collections';
};
export type C2CConnectionsProps = {
  id: string;
  type: 'c2c-connections';
};
export type RootFolderProps = {
  expandable?: boolean;
  id: string;
  type: 'root-folder';
};
export type TrashProps = {
  id: string;
  type: 'trash';
};
export type UploadLinksProps = {
  id: string;
  type: 'upload-links';
};

export type FolderProps = {
  id: string;
  isBoundary: boolean;
  isLoading: boolean;
  parentId: string;
  type: 'folder';
};

export type CollectionProps = {
  id: string;
  isBoundary: boolean;
  type: 'collection';
};

// union for all the different item prop types
export type ProjectItemProps =
  | AllCollectionsProps
  | C2CConnectionsProps
  | CollectionProps
  | FolderProps
  | HiddenRootProps
  | RootFolderProps
  | TrashProps
  | UploadLinksProps;
// wraps the item props types with the extra information provided by the WindowedTreeView
export type ProjectTreeItemProps = TreeItemProps<ProjectItemProps>;

// convenience types for all the props passed to each type of ProjectTree item
export type AllCollectionsTreeItemProps = Extract<
  ProjectTreeItemProps,
  { type: 'all-collections' }
>;
export type C2CConnectionsTreeItemProps = Extract<
  ProjectTreeItemProps,
  { type: 'c2c-connections' }
>;
export type CollectionTreeItemProps = Extract<
  ProjectTreeItemProps,
  { type: 'collection' }
>;
export type FolderTreeItemProps = Extract<
  ProjectTreeItemProps,
  { type: 'folder' }
>;
export type RootFolderTreeItemProps = Extract<
  ProjectTreeItemProps,
  { type: 'root-folder' }
>;
export type TrashTreeItemProps = Extract<
  ProjectTreeItemProps,
  { type: 'trash' }
>;
export type UploadLinksTreeItemProps = Extract<
  ProjectTreeItemProps,
  { type: 'upload-links' }
>;

export type WindowedProjectTree = Tree<ProjectItemProps>;
export type WindowedProjectTreeProps = WindowedTreeViewProps<ProjectItemProps>;
